import { z } from 'zod';

import { Grid, Hero, ProTheme, StatusBar } from './layout.js';
import { DayOfWeek, TimeOfDay, Uuid } from './primitives.js';
import { Tile } from './tiles.js';

/**
 * A complete launcher configuration: grid + theme + status bar. Pro tier
 * supports multiple profiles (multiProfile flag); Core ignores all but
 * `defaultProfileId`.
 *
 * `tools` is an optional list of tile entries surfaced in the launcher's
 * Tools modal alongside the universal built-ins (Device Info, DNS &
 * Ping, Calculator). Pro+ feature (`customTools`) — Core configs may
 * include the field, but the runtime only renders it when entitled.
 */
export const Profile = z.object({
  id: Uuid,
  name: z.string().min(1).max(60),
  grid: Grid,
  theme: ProTheme,
  statusBar: StatusBar,
  /**
   * Tools modal entries — built-in Device Info / DNS-Ping / Calculator
   * are always present; this list adds custom tools. Divider tiles
   * make no sense in a modal list and are rejected.
   */
  tools: z
    .array(Tile)
    .max(12)
    .default([])
    .refine((tiles) => tiles.every((t) => t.type !== 'divider'), {
      message: 'tools must not contain divider tiles',
    }),
  /**
   * Hero band variant + its content. Optional; when undefined the
   * launcher renders the `featured` variant (uses `featuredTiles`
   * below), preserving the behavior of every config from before this
   * field existed.
   */
  hero: Hero.optional(),
  /**
   * Up to 4 tiles surfaced in the launcher's hero band — rendered
   * larger, centered horizontally, hovering on the hero background.
   * Available at every tier; the hero background image
   * (`theme.heroImageRef`) is the Pro/Ent piece.
   *
   * Spacer / folder / divider tiles aren't allowed here — featured
   * slots are meant to advertise an action, not pad space or
   * separate rows. Surfaces as a Zod issue if one slips in.
   */
  featuredTiles: z
    .array(Tile)
    .max(4)
    .default([])
    .refine(
      (tiles) =>
        tiles.every(
          (t) => t.type !== 'spacer' && t.type !== 'folder' && t.type !== 'divider',
        ),
      { message: 'featuredTiles must not contain spacer, folder, or divider tiles' },
    ),
});
export type Profile = z.infer<typeof Profile>;

/**
 * Time-windowed profile activation (Pro; scheduledProfiles flag).
 * Resolution: in array order, first matching schedule wins; otherwise
 * fall through to `defaultProfileId`. Timezone is the device's local TZ.
 */
export const ProfileSchedule = z.object({
  id: Uuid,
  profileId: Uuid,
  daysOfWeek: z.array(DayOfWeek).min(1).max(7),
  startTime: TimeOfDay,
  endTime: TimeOfDay,
});
export type ProfileSchedule = z.infer<typeof ProfileSchedule>;
